import React, { useState } from 'react';
import { Info, ShieldAlert, Trash2 } from 'lucide-react';
import Card from '../../../components/Card';
import { CONTRACT_TYPE_FALLBACK_OPTIONS } from '../../../constants/contracts';
import { Badge, SaveResetActions, META_LABEL } from './editorBits';

const FIELDS = [
  { key: 'risks', label: 'Risks', placeholder: 'What the AI should flag as a risk for this scope…' },
  { key: 'commitments', label: 'Commitments', placeholder: 'What Lightspark obligations to surface…' },
  { key: 'financials', label: 'Financial terms', placeholder: 'What money terms to surface…' },
];

const hasContent = (obj) => FIELDS.some((f) => (obj?.[f.key] || '').trim());

// The three guidance fields for the selected scope.
function TripleFields({ value = {}, onChange }) {
  return (
    <div className="stack-md">
      {FIELDS.map((f) => (
        <div key={f.key}>
          <div style={META_LABEL}>{f.label}</div>
          <textarea
            className="textarea"
            value={value[f.key] || ''}
            onChange={(e) => onChange(f.key, e.target.value)}
            placeholder={f.placeholder}
            style={{ width: '100%', minHeight: 150, resize: 'vertical', lineHeight: 1.55 }}
          />
        </div>
      ))}
    </div>
  );
}

function RailItem({ label, active, dot, onClick }) {
  return (
    <button type="button" className={`pb-rail-item ${active ? 'active' : ''}`} onClick={onClick} title={label}>
      <span className="label">{label}</span>
      {dot && <span className="dot" />}
    </button>
  );
}

// Pick-to-add control: choosing an option adds that scope and selects it.
function AddSelect({ label, options, onAdd }) {
  if (options.length === 0) return null;
  return (
    <select
      className="select"
      value=""
      onChange={(e) => { if (e.target.value) onAdd(e.target.value); }}
      style={{ width: '100%', marginTop: 2 }}
    >
      <option value="">{label}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// Contract-playbook editor — master/detail. A global Default plus a guidance
// playbook per contract type and per category; pick a scope on the left, edit
// its risks / commitments / financial-terms prompts on the right. Scopes layer
// additively (Default + contract type) at extraction time.
export default function KeyRiskCriteriaEditor({ ctl }) {
  const d = ctl.draft || {};
  const def = d.default || {};
  const byType = d.byType || {};
  const [sel, setSel] = useState({ kind: 'default' });

  const setDefault = (field, val) => ctl.setField('default', { ...def, [field]: val });
  const setScoped = (scope, key, field, val) => {
    const map = d[scope] || {};
    ctl.setField(scope, { ...map, [key]: { ...(map[key] || {}), [field]: val } });
  };
  const addScoped = (scope, key) => {
    const map = d[scope] || {};
    if (!map[key]) ctl.setField(scope, { ...map, [key]: {} });
  };
  const removeScoped = (scope, key) => {
    const map = { ...(d[scope] || {}) };
    delete map[key];
    ctl.setField(scope, map);
  };

  const typeKeys = Object.keys(byType);
  const typeAddOptions = CONTRACT_TYPE_FALLBACK_OPTIONS.map((o) => o.value).filter((t) => !byType[t]);

  // Resolve the selected scope to its value, change handler, and chrome.
  let title;
  let hint;
  let value;
  let onFieldChange;
  let onRemove = null;
  if (sel.kind === 'type' && byType[sel.key]) {
    title = sel.key;
    hint = `Extra guidance for ${sel.key} contracts, layered on top of Default.`;
    value = byType[sel.key];
    onFieldChange = (field, val) => setScoped('byType', sel.key, field, val);
    onRemove = () => { removeScoped('byType', sel.key); setSel({ kind: 'default' }); };
  } else {
    title = 'Default';
    hint = 'Applies to every contract, whatever its type.';
    value = def;
    onFieldChange = setDefault;
  }

  return (
    <Card
      title="Contract playbooks"
      icon={ShieldAlert}
      action={<SaveResetActions dirty={ctl.dirty} saving={ctl.saving} onSave={ctl.save} onReset={ctl.reset} />}
    >
      <div className="stack-md">
        <div className="callout blue">
          <div className="callout-icon"><Info className="w-4 h-4" /></div>
          <div className="callout-body" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            Guidance for what the AI highlights on a contract — <strong>risks</strong>, <strong>commitments</strong>, and{' '}
            <strong>financial terms</strong>. <strong>Default</strong> applies to every contract; add a contract type
            to tune it. They <strong>stack</strong> (Default + type). Edits apply on the next scan or when someone
            clicks Regenerate.
            <div style={{ marginTop: 6 }}>
              <Badge isDefault={ctl.base?.isDefault} updatedAt={ctl.base?.updatedAt} />
            </div>
          </div>
        </div>

        {/* Grid and divider are CLASSES, not inline styles: stacked-narrow needs a
            media query, which an inline style cannot carry (shared with the Data
            Schemas workbench — see components.css `.pb-workbench`). */}
        <div className="pb-workbench">
          {/* Scope rail */}
          <div className="stack-md pb-rail">
            <RailItem label="Default" active={sel.kind === 'default'} dot={hasContent(def)} onClick={() => setSel({ kind: 'default' })} />
            <div>
              <div style={{ ...META_LABEL, marginBottom: 6 }}>Contract types</div>
              <div className="stack-sm">
                {/* Only the type rows are height-capped when stacked; the Add
                    picker stays a sibling BELOW so it never scrolls out of reach. */}
                {typeKeys.length > 0 && (
                  <div className="stack-sm pb-rail-list">
                    {typeKeys.map((t) => (
                      <RailItem key={t} label={t} active={sel.kind === 'type' && sel.key === t} dot={hasContent(byType[t])} onClick={() => setSel({ kind: 'type', key: t })} />
                    ))}
                  </div>
                )}
                <AddSelect label="Add a contract type…" options={typeAddOptions} onAdd={(t) => { addScoped('byType', t); setSel({ kind: 'type', key: t }); }} />
              </div>
            </div>
          </div>

          {/* Selected scope */}
          <div className="stack-md">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
              {onRemove && (
                <button type="button" className="btn ghost xs" onClick={onRemove} style={{ color: 'var(--text-critical)' }} title="Remove this scope's guidance">
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </button>
              )}
            </div>
            <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>{hint}</div>
            <TripleFields value={value} onChange={onFieldChange} />
          </div>
        </div>
      </div>
    </Card>
  );
}
